import { Loader, Pill, Tabs, Typography } from '@recruit-robin/robin-theme/web-components';
import { Check, Database_01, File_05, Indeed } from '@recruit-robin/robin-theme/web-icons';
import { memo, useCallback, useContext } from 'react';
import { useContextSelector } from 'use-context-selector';

import { useIsMutating } from '@tanstack/react-query';
import { useGTM } from 'hooks/gtm';
import { LocalizationContext } from 'shared/legacy/contexts/LocalizationContext/LocalizationContext';
import { useHasValidAtsConnectionQuery } from 'shared/legacy/hooks/useHasValidAtsConnectionQuery';
import { QueryKey } from 'types/query';
import { useEnrichedVacancyModalSettings } from '../../hooks/useEnrichedVacancyModalSettings';
import { EnrichVacancyModalContext } from '../EnrichVacancyModalContext/EnrichVacancyModalContext';
import { EnrichVacancyModalType } from '../EnrichVacancyModalContext/EnrichVacancyModalContext.types';
import { LoadingPage } from '../LoadingPage/LoadingPage';
import { useStyles } from './EditEnrichedVacancy.styles';
import { EnrichTab } from './EditEnrichedVacancy.types';
import { EditEnrichedVacancyContext } from './EditEnrichedVacancyContext/EditEnrichedVacancyContext';
import { EnrichedVersion } from './EnrichedVersion/EnrichedVersion';
import { JobDescription } from './JobDescription/JobDescription';
import { JobPreviewLazy } from './JobPreview/JobPreview.lazy';
import { RenderTabs } from './RenderTabs/RenderTabs';
import { useGenerate } from './useGenerate';

export const EditEnrichedVacancy = memo(() => {
  const classes = useStyles();
  const { dictionary } = useContext(LocalizationContext);
  const { data: hasValidAts } = useHasValidAtsConnectionQuery();
  const activeTab = useContextSelector(EnrichVacancyModalContext, (s) => s.editVacancyTab);
  const setActiveTab = useContextSelector(EnrichVacancyModalContext, (s) => s.setEditVacancyTab);
  const modalType = useContextSelector(EnrichVacancyModalContext, (s) => s.modalType);
  const enrichedVersionRef = useContextSelector(EditEnrichedVacancyContext, (s) => s.enrichedVersion.ref);
  const jobPreviewRef = useContextSelector(EditEnrichedVacancyContext, (s) => s.jobPreview.ref);
  const vacancy = useContextSelector(EnrichVacancyModalContext, (s) => s.vacancy);
  const gtm = useGTM();

  const { handleGenerateJobPreview, handleGenerateEnrichedVacancy } = useGenerate();

  const { isJobPreviewTabVisible, isJobDescriptionTabVisible, isJobPreviewFeatureEnabled } =
    useEnrichedVacancyModalSettings();

  const isGeneratingJobPreview = useIsMutating([QueryKey.useGenerateJobPreviewMutation]) > 0;
  const isGeneratingEnrichedVacancy = useIsMutating([QueryKey.useGenerateEnrichedVersionMutation]) > 0;

  const handleValueChange = useCallback(
    (value: EnrichTab) => {
      setActiveTab((prevTab) => {
        if (!isJobPreviewFeatureEnabled) {
          return value;
        }

        const shouldGenerateJobPreview =
          prevTab === EnrichTab.EnrichedVersion &&
          value === EnrichTab.JobPreview &&
          (enrichedVersionRef.current?.getIsDirty() || jobPreviewRef.current?.getValue() === '');

        const shouldGenerateEnrichedVacancy =
          prevTab === EnrichTab.JobPreview &&
          value === EnrichTab.EnrichedVersion &&
          jobPreviewRef.current?.getIsDirty();

        if (shouldGenerateJobPreview) {
          handleGenerateJobPreview();
        }

        if (shouldGenerateEnrichedVacancy) {
          handleGenerateEnrichedVacancy();
        }

        return value;
      });
    },
    [handleGenerateJobPreview, handleGenerateEnrichedVacancy, isJobPreviewFeatureEnabled],
  );

  return (
    <Tabs.Root customCss={classes.tabContext} value={activeTab} onValueChange={handleValueChange} variant="background">
      <Tabs.Indicator />
      <Tabs.List customCss={classes.tabButtonGroup}>
        {isJobDescriptionTabVisible && (
          <Tabs.Trigger customCss={classes.tabButton} value={EnrichTab.JobDescription}>
            {modalType === EnrichVacancyModalType.Indeed ? <Indeed size={16} /> : <Database_01 size={16} />}

            <Typography variant={(v) => v.heading[5]}>
              {modalType === EnrichVacancyModalType.Indeed ? dictionary.indeedJob : dictionary.atsVersion}
            </Typography>

            {!hasValidAts && modalType === EnrichVacancyModalType.Ats && (
              <Pill
                label={dictionary.atsDisconnected}
                style="icon-removable"
                type="error-light"
                size="small"
                padding="text"
              />
            )}
          </Tabs.Trigger>
        )}

        <Tabs.Trigger customCss={classes.tabButton} value={EnrichTab.EnrichedVersion}>
          <Check size={16} />
          <Typography variant={(v) => v.heading[5]}>{dictionary.enrichedVersion}</Typography>

          {isGeneratingEnrichedVacancy && <Loader size={14} variant="typing-1" animateEntrance />}
        </Tabs.Trigger>

        {isJobPreviewTabVisible && isJobPreviewFeatureEnabled && (
          <Tabs.Trigger
            customCss={classes.tabButton}
            value={EnrichTab.JobPreview}
            onClick={() =>
              gtm.jobPreviewClicked({
                jobImprovedId: vacancy?.db_id,
                atsId: vacancy?.job_ats_id,
                indeedId: vacancy?.job_indeed_id,
              })
            }>
            <File_05 size={16} />
            <Typography variant={(v) => v.heading[5]}>{dictionary.jobPreview}</Typography>

            {isGeneratingJobPreview && <Loader size={14} variant="typing-1" animateEntrance />}
          </Tabs.Trigger>
        )}

        <RenderTabs activeTab={activeTab} />
      </Tabs.List>

      {isJobDescriptionTabVisible && (
        <Tabs.Content customCss={classes.tabPanel} value={EnrichTab.JobDescription}>
          <JobDescription />
        </Tabs.Content>
      )}

      <Tabs.Content customCss={classes.tabPanel} value={EnrichTab.EnrichedVersion} forceMount>
        {isGeneratingEnrichedVacancy && activeTab === EnrichTab.EnrichedVersion ? (
          <LoadingPage message={dictionary.updatingJobPreview} />
        ) : (
          <EnrichedVersion focusFormTab={() => setActiveTab(EnrichTab.EnrichedVersion)} />
        )}
      </Tabs.Content>

      {isJobPreviewTabVisible && isJobPreviewFeatureEnabled && (
        <Tabs.Content customCss={classes.tabPanel} value={EnrichTab.JobPreview} forceMount>
          {isGeneratingJobPreview && activeTab === EnrichTab.JobPreview ? (
            <LoadingPage message={dictionary.updatingJobPreview} />
          ) : (
            <JobPreviewLazy />
          )}
        </Tabs.Content>
      )}
    </Tabs.Root>
  );
});
